import React, { useEffect, useRef, useState } from "react";
import { Button, Descriptions, Input, Modal, Spin, notification } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  useDeleteTaskExecutionFileMutation,
  useGetTaskExecutionByIdQuery,
  useUpdateTaskExecutionMutation,
  useUploadTaskExecutionFileMutation,
} from "shared/api";
import { handleRequestError } from "shared/helpers";
import { FileAttachmentsList } from "./FileAttachmentsList";

interface CompleteTaskModalProps {
  open: boolean;
  taskExecutionId: number | null;
  onClose: () => void;
}

const COMPLETED_STATUS = "COMPLETED";

const formatDate = (value?: string | null) =>
  value && dayjs(value).isValid()
    ? dayjs(value).format("YYYY-MM-DD HH:mm")
    : "-";

const getFileName = (url: string) => {
  try {
    const cleanUrl = url.split("?")[0];
    return decodeURIComponent(cleanUrl.split("/").pop() || "Unnamed file");
  } catch {
    return "Unnamed file";
  }
};

export const CompleteTaskModal: React.FC<CompleteTaskModalProps> = ({
  open,
  taskExecutionId,
  onClose,
}) => {
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState("");
  const [newFiles, setNewFiles] = useState<File[]>([]);

  const { data: detail, isFetching } = useGetTaskExecutionByIdQuery(
    {
      id: taskExecutionId as number,
      expand: "files,task,task.files,machine,user",
    },
    { skip: !open || taskExecutionId == null }
  );

  const [updateTaskExecution, { isLoading: isSaving }] =
    useUpdateTaskExecutionMutation();
  const [uploadFile, { isLoading: isUploading }] =
    useUploadTaskExecutionFileMutation();
  const [deleteFile] = useDeleteTaskExecutionFileMutation();

  // Sync the message from the loaded detail; reset staged state on close.
  useEffect(() => {
    if (detail) setMessage(detail.message ?? "");
  }, [detail]);

  useEffect(() => {
    if (!open) {
      setMessage("");
      setNewFiles([]);
    }
  }, [open]);

  const attachedFiles = detail?.files ?? [];
  const taskFiles = detail?.task?.files ?? [];
  const userName =
    detail && typeof detail.user === "object" ? detail.user.username : "-";

  const handleAttachFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files) return;
    setNewFiles((prev) => [...prev, ...Array.from(files)]);
    e.target.value = "";
  };

  const handleRemoveNewFile = (index: number) =>
    setNewFiles((prev) => prev.filter((_, i) => i !== index));

  const handleDeleteFile = async (fileId: number) => {
    if (taskExecutionId == null) return;
    try {
      await deleteFile({ id: taskExecutionId, fileId }).unwrap();
    } catch (error) {
      api.error({
        message:
          t("taskHub.myTasks.fileDeleteError") || "Failed to delete file",
        description: handleRequestError(error, ""),
      });
    }
  };

  /** Upload staged files, patch the message and (optionally) mark completed. */
  const persist = async (complete: boolean) => {
    if (taskExecutionId == null) return;
    try {
      if (newFiles.length > 0) {
        await Promise.all(
          newFiles.map((file) =>
            uploadFile({ id: taskExecutionId, file }).unwrap()
          )
        );
        setNewFiles([]);
      }

      await updateTaskExecution({
        id: taskExecutionId,
        data: complete ? { message, status: COMPLETED_STATUS } : { message },
      }).unwrap();

      api.success({
        message: complete
          ? t("taskHub.myTasks.completeSuccess") || "Task completed"
          : t("taskHub.myTasks.draftSuccess") || "Draft saved",
      });
      onClose();
    } catch (error) {
      api.error({
        message: t("taskHub.myTasks.saveError") || "Failed to save",
        description: handleRequestError(error, ""),
      });
    }
  };

  const busy = isSaving || isUploading;
  const disabled = busy || isFetching || !detail;

  return (
    <Modal
      title={t("taskHub.myTasks.completeTask") || "Complete task"}
      open={open}
      onCancel={onClose}
      width={720}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={busy}>
          {t("Cancel") || "Cancel"}
        </Button>,
        <Button
          key="draft"
          onClick={() => persist(false)}
          loading={busy}
          disabled={disabled}
        >
          {t("taskHub.myTasks.saveDraft") || "Save draft"}
        </Button>,
        <Button
          key="complete"
          type="primary"
          style={{ backgroundColor: themeColor, borderColor: themeColor }}
          onClick={() => persist(true)}
          loading={busy}
          disabled={disabled}
        >
          {t("taskHub.myTasks.completeTask") || "Complete task"}
        </Button>,
      ]}
    >
      {contextHolder}

      {isFetching || !detail ? (
        <div className="flex justify-center py-10">
          <Spin />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label={t("taskHub.logTable.task") || "Task"}>
              {detail.task?.name ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("taskHub.logTable.expectedTime") || "Expected time"}
            >
              {formatDate(detail.timestamp_expected)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("taskHub.logTable.executedTime") || "Executed time"}
            >
              {formatDate(detail.timestamp_executed)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("taskHub.logTable.machine") || "Machine"}
            >
              {detail.machine?.name ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("taskHub.logTable.user") || "User"}>
              {userName}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("taskHub.myTasks.taskFiles") || "Task files"}
            >
              {taskFiles.length > 0 ? (
                <ul className="space-y-2">
                  {taskFiles.map((file) => (
                    <li
                      key={file.id}
                      className="flex items-center gap-1 rounded border bg-gray-50 p-2"
                    >
                      <span title={getFileName(file.file)}>
                        {getFileName(file.file)}
                      </span>
                      <a
                        href={file.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={getFileName(file.file)}
                        title={t("taskHub.modal.downloadFile") || ""}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <DownloadOutlined className="text-xl" />
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-gray-400">
                  {t("taskHub.myTasks.noFiles") || "No files"}
                </span>
              )}
            </Descriptions.Item>
          </Descriptions>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="font-semibold">
                {t("taskHub.myTasks.files") || "Files"}
              </span>
              <input
                type="file"
                multiple
                ref={fileInputRef}
                onChange={handleAttachFiles}
                className="!hidden"
              />
              <Button
                type="primary"
                style={{ backgroundColor: themeColor, borderColor: themeColor }}
                onClick={() => fileInputRef.current?.click()}
              >
                {t("taskHub.modal.attachFiles") || "Attach files"}
              </Button>
            </div>
            <FileAttachmentsList
              attachedFiles={attachedFiles}
              newFiles={newFiles}
              themeColor={themeColor}
              onDeleteFile={handleDeleteFile}
              onRemoveNewFile={handleRemoveNewFile}
            />
          </div>

          <div>
            <div className="mb-2 font-semibold">
              {t("taskHub.myTasks.message") || "Message"}
            </div>
            <Input.TextArea
              rows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                t("taskHub.myTasks.messagePlaceholder") || "Enter a message..."
              }
            />
          </div>
        </div>
      )}
    </Modal>
  );
};
